import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import chatAPI from "../../services/chatService";
import useFloatingDrag from "../../hooks/useFloatingDrag";

// Use favicon as the Ask Dythi logo/icon
const LOGO_SRC = "/favicon.ico";

const WELCOME_MESSAGE =
  "Namaste! I'm Dythi. Where every breath finds its rhythm. Welcome to Divyyog Foundation — how can I illuminate your wellness path today?";

const SUGGESTIONS = [
  "What plans do you offer?",
  "Is there a free trial?",
  "What if I miss a live class?",
];

// ── 3D floating bubble: tracks the cursor and tilts toward it, like the site's
//    other magnetic/tilt cards, so the trigger button feels physically present. ──
function Bubble3D({ onClick, onPointerDown, hasUnread }) {
  const ref = useRef(null);
  const rotateX = useSpring(useMotionValue(0), { stiffness: 200, damping: 18 });
  const rotateY = useSpring(useMotionValue(0), { stiffness: 200, damping: 18 });
  const [imgOk, setImgOk] = useState(true);
  const reduceMotion = useReducedMotion();

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    rotateY.set(((e.clientX - r.left) / r.width - 0.5) * 26);
    rotateX.set(((e.clientY - r.top) / r.height - 0.5) * -26);
  };
  const handleLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <motion.button
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={onClick}
      onPointerDown={onPointerDown}
      initial={{ opacity: 0, scale: 0.6, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      whileTap={{ scale: 0.92 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      aria-label="Open Ask Dythi chat"
      title="Tap to chat · drag to move"
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        transformPerspective: 800,
        touchAction: "none",
      }}
      className="relative w-14 md:w-20 flex flex-col items-center gap-1 cursor-pointer active:cursor-grabbing outline-none group select-none"
    >
      <div className="relative w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center">
        {/* soft ambient glow beneath the bubble */}
        <div
          className="absolute -inset-5 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(242,154,12,0.7) 0%, transparent 72%)",
            filter: "blur(18px)",
          }}
        />

        {/* gold halo hugging the border, blinking to draw the eye */}
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            translateZ: 22,
            boxShadow:
              "0 0 0 3px rgba(242,154,12,0.95), 0 0 26px 8px rgba(242,154,12,0.9), 0 0 54px 20px rgba(242,154,12,0.55)",
          }}
          animate={
            reduceMotion
              ? { opacity: 0.9 }
              : { opacity: [0.2, 1, 0.2], scale: [0.99, 1.1, 0.99] }
          }
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
          }
        />

        {/* outer ring pinging outward, adding depth to the blink */}
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            translateZ: 18,
            boxShadow: "0 0 0 2px rgba(242,154,12,0.7)",
          }}
          animate={
            reduceMotion
              ? { opacity: 0 }
              : { opacity: [0.75, 0], scale: [1, 1.45] }
          }
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: 1.5, repeat: Infinity, ease: "easeOut" }
          }
        />

        <div
          className="relative w-full h-full rounded-full flex items-center justify-center overflow-hidden border-2 border-white/70"
          style={{
            background: "linear-gradient(145deg,#3a2200,#1a0e00)",
            boxShadow:
              "0 0 0 3px rgba(242,154,12,0.8), 0 0 30px 8px rgba(242,154,12,0.7), 0 20px 45px rgba(242,154,12,0.5), 0 4px 10px rgba(0,0,0,0.35), inset 0 1px 2px rgba(255,255,255,0.25)",
            transform: "translateZ(24px)",
          }}
        >
          {imgOk ? (
            <img
              src={LOGO_SRC}
              alt="Ask Dythi"
              onError={() => setImgOk(false)}
              className="w-full h-full object-cover"
              style={{ transform: "translateZ(10px)" }}
            />
          ) : (
            <span className="text-3xl" style={{ transform: "translateZ(10px)" }}>
              🪷
            </span>
          )}
        </div>

        {hasUnread && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#F29A0C] border-2 border-white animate-pulse" />
        )}
      </div>

      {/* Badge below the icon */}
      <span
        className="px-2 md:px-2.5 py-0.5 rounded-full text-[9px] md:text-[11px] font-semibold text-white tracking-tight shadow-md border border-white/30"
        style={{
          background: "linear-gradient(135deg, #F29A0C, #FF7A00)",
          boxShadow: "0 4px 14px rgba(242,154,12,0.45)",
          transform: "translateZ(15px)",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        Ask Dythi
      </span>
    </motion.button>
  );
}

function FormattedMessage({ content, isUser }) {
  if (!content) return null;

  if (isUser) {
    return <div className="whitespace-pre-wrap">{content}</div>;
  }

  // Remove em-dashes and en-dashes from content
  const sanitized = content
    .replace(/\s*[—–]\s*/g, " : ")
    .replace(/(?<=\S)\s+-\s+(?=\S)/g, " : ");

  const lines = sanitized.split("\n");

  const renderInline = (str) => {
    const parts = str.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, pIdx) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={pIdx} className="font-semibold text-[#1a0e00]">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  };

  return (
    <div className="space-y-2 text-[13.5px] leading-[22px] text-[#2c1d0c]">
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={idx} className="h-1" />;

        const isIndented = line.startsWith("  ") || line.startsWith("\t");
        const isBullet = /^[-*•–—]\s+/.test(trimmed);
        const numberMatch = trimmed.match(/^(\d+)\.\s+(.*)/);

        if (isBullet) {
          const cleanText = trimmed.replace(/^[-*•–—]+\s*/, "");
          return (
            <div
              key={idx}
              className={`flex items-start gap-2.5 my-1 ${
                isIndented ? "pl-4 text-[13px] text-[#554332]" : "pl-1"
              }`}
            >
              <span
                className={`text-[#F29A0C] font-bold ${
                  isIndented ? "text-[10px] mt-0.5" : "text-[13px] leading-[20px]"
                }`}
              >
                •
              </span>
              <div className="flex-1">{renderInline(cleanText)}</div>
            </div>
          );
        }

        if (numberMatch) {
          return (
            <div key={idx} className="flex items-start gap-2.5 pl-1 my-1.5">
              <span className="text-[#F29A0C] font-bold text-[13px] min-w-[18px] leading-[22px]">
                {numberMatch[1]}.
              </span>
              <div className="flex-1">{renderInline(numberMatch[2])}</div>
            </div>
          );
        }

        return <p key={idx}>{renderInline(trimmed)}</p>;
      })}
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-[#F29A0C]"
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

export default function AskDivy() {
  const [open, setOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: WELCOME_MESSAGE },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const floatDrag = useFloatingDrag("dy_float_chat_pos");
  const { ensureVisible } = floatDrag;

  // When the chat window opens, slide the widget back on-screen if the user
  // had dragged the bubble near an edge.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => ensureVisible(true), 340);
    return () => clearTimeout(t);
  }, [open, ensureVisible]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  const toggleOpen = () => {
    setOpen((o) => !o);
    setHasUnread(false);
  };

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;

    const nextMessages = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setInput("");
    setSending(true);

    try {
      // Send recent history (excluding welcome message and current user turn)
      const history = nextMessages.slice(1, -1).slice(-10);
      const { data } = await chatAPI.ask(trimmed, history);
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
      if (!open) setHasUnread(true);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I'm having trouble reaching our servers right now. Please try again in a moment, or use the Contact page.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
    {/* Drag bounds: the visible screen, with a small margin */}
    <div ref={floatDrag.constraintsRef} className="fixed inset-2 pointer-events-none" aria-hidden="true" />
    <motion.div
      ref={floatDrag.elRef}
      {...floatDrag.dragProps}
      style={{ x: floatDrag.x, y: floatDrag.y }}
      className="fixed bottom-[5.5rem] right-3.5 md:bottom-6 md:right-6 z-[95] flex flex-col items-end">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.92, rotateX: -8 }}
            animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
            exit={{ opacity: 0, y: 20, scale: 0.94, rotateX: -6 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformPerspective: 1200, transformOrigin: "bottom right" }}
            className="mb-5 w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] rounded-[28px] overflow-hidden flex flex-col border border-[#F4D9B5]"
            role="dialog"
            aria-label="Ask Dythi chat"
          >
            <div
              className="relative"
              style={{
                boxShadow:
                  "0 40px 100px rgba(0,0,0,0.28), 0 10px 30px rgba(242,154,12,0.2)",
              }}
            >
              {/* Header */}
              <div
                className="flex items-center gap-3 px-5 py-4"
                style={{
                  background: "linear-gradient(120deg,#1a0e00,#3a2200)",
                }}
              >
                <div className="w-10 h-10 rounded-full overflow-hidden border border-white/30 flex items-center justify-center bg-black/20 flex-shrink-0">
                  <img
                    src={LOGO_SRC}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-[15px] leading-tight" style={{ fontFamily: "Poppins" }}>
                    Dythi
                  </p>
                  <p className="text-white/60 text-[11px] flex items-center gap-1.5 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#6ee08a]" />
                    Wellness assistant
                  </p>
                </div>
                <button
                  onClick={toggleOpen}
                  aria-label="Close chat"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <FaTimes size={13} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-5 space-y-4"
              style={{ background: "linear-gradient(180deg,#FFF8EE,#FFFDF8)" }}
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[88%] px-4 py-3 text-[13.5px] leading-[21px] ${
                      m.role === "user"
                        ? "rounded-[18px] rounded-br-[4px] text-white"
                        : "rounded-[18px] rounded-bl-[4px] bg-white border border-[#F4D9B5]"
                    }`}
                    style={
                      m.role === "user"
                        ? {
                            background: "linear-gradient(135deg,#F29A0C,#FF7A00)",
                            boxShadow: "0 8px 22px rgba(242,154,12,0.28)",
                          }
                        : { boxShadow: "0 6px 20px rgba(0,0,0,0.04)" }
                    }
                  >
                    <FormattedMessage content={m.content} isUser={m.role === "user"} />
                  </div>
                </div>
              ))}

              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-[11.5px] font-semibold text-[#F29A0C] bg-[#F29A0C]/10 border border-[#F29A0C]/25 rounded-full px-3.5 py-2 hover:bg-[#F29A0C]/20 transition-colors cursor-pointer"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {sending && (
                <div className="flex justify-start">
                  <div className="rounded-[18px] rounded-bl-[4px] bg-white border border-[#F4D9B5]">
                    <TypingDots />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 bg-white border-t border-[#F4D9B5] flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about classes, pricing, plans…"
                disabled={sending}
                className="flex-1 h-11 rounded-full border border-[#F4D9B5] bg-[#FFF8EE] px-4 text-[13.5px] text-[#111] placeholder:text-[#999] outline-none focus:border-[#F29A0C] transition-colors"
              />
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => send()}
                disabled={sending || !input.trim()}
                aria-label="Send message"
                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-white disabled:opacity-40 transition-opacity cursor-pointer"
                style={{
                  background: "linear-gradient(135deg,#F29A0C,#FF7A00)",
                  boxShadow: "0 10px 26px rgba(242,154,12,0.35)",
                }}
              >
                <FaPaperPlane size={13} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Bubble3D
        onClick={floatDrag.guardClick(toggleOpen)}
        onPointerDown={floatDrag.startDrag}
        hasUnread={hasUnread}
      />
    </motion.div>
    </>
  );
}
